import { PureComponent, Fragment, createRef } from 'react';
import { withTranslation } from 'react-i18next';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import moment from 'moment';

import {
  AvForm,
  AvField,
  AvCheckboxGroup,
  AvCheckbox,
  AvRadio,
  AvRadioGroup,
} from 'availity-reactstrap-validation';

import {
  Grid,
  Segment,
  Modal,
  Button,
  Icon,
  Dropdown,
} from 'semantic-ui-react';

import { CKEditor } from '@ckeditor/ckeditor5-react';

import {
  ClassicEditor,
  Bold,
  Essentials,
  Italic,
  Paragraph,
  List,
  Heading,
} from 'ckeditor5';

import { map, get } from '../../../utils/lodash';

import {
  EmptyContainer,
  CustomTable,
  SlideoutPanel,
} from '../../../components';

import {
  getBroadcastNotifications,
  createBroadcastNotifications,
  getNotifications,
  getOrganizationUsers,
  updateNotificationReadStatus,
} from '../../../store/actions';

import { HtmlService } from '../../../services';
import { INSTANCE_CONTACT_EMAIL } from '../../../utils/instanceConstant';

import './organization.scss';

class NotificationForm extends PureComponent {
  constructor(props) {
    super(props);
    this.state = {
      userType: 'allUsers',
      users: [],
      usersError: false,
      message: '',
      messageError: false,
      style: 'info',
      sendAsEmail: true,
      sendToDashboard: true,
      targetsError: false,
    };
  }

  submit() {
    this.form && this.form.submit();
  }

  async handleValidSubmit(event, values) {
    try {
      const { users, message, sendAsEmail, sendToDashboard } = this.state;
      const { toggle, createBroadcastNotifications, resetPage } = this.props;
      const isMessageEmpty = !message || message === '<p>&nbsp;</p>';
      const isTargetsEmpty = !sendAsEmail && !sendToDashboard;
      const isUsersEmpty = values.userType === 'selectedUsers' && !users.length;
      if (isUsersEmpty || isMessageEmpty || isTargetsEmpty) {
        this.setState({
          usersError: isUsersEmpty,
          messageError: isMessageEmpty,
          targetsError: isTargetsEmpty,
        });
      } else {
        const formData = {
          title: values.title,
          message,
          style: values.style,
          sendAsEmail,
          sendToDashboard,
          isOwnOrganisation: true,
          isAllUsers: values.userType === 'allUsers',
          isOnlyAdmin: values.userType === 'orgAdmins',
          isTestToMyself: values.userType === 'testToMyself',
        };
        if (values.userType === 'selectedUsers') {
          formData.users = users;
        }

        // deliberately not resetting the form here — matches the site-admin
        // broadcast dialog, since similar messages are often sent in a row
        await createBroadcastNotifications(formData);
        if (resetPage) resetPage();
        toggle();
      }
    } catch (error) {
      console.error(
        'Error: createBroadcastNotifications ~ handleValidSubmit',
        error
      );
    }
  }

  getUsersOptions() {
    let { orgUsers } = this.props;
    return map(orgUsers, (item) => ({
      key: item.id,
      value: item.id,
      text: item.name,
    }));
  }

  onChangeUsers = (event, data) => {
    this.setState({
      users: data.value,
      usersError: !data.value.length,
    });
  };

  sendAsEmailToggle = () => {
    this.setState((prevState) => ({
      sendAsEmail: !prevState.sendAsEmail,
      targetsError: false,
    }));
  };

  sendToDashboardToggle = () => {
    this.setState((prevState) => ({
      sendToDashboard: !prevState.sendToDashboard,
      targetsError: false,
    }));
  };

  render() {
    const {
      usersError,
      messageError,
      sendAsEmail,
      sendToDashboard,
      targetsError,
    } = this.state;
    const { t } = this.props;
    const options = this.getUsersOptions();

    return (
      <AvForm
        ref={(c) => (this.form = c)}
        onValidSubmit={(event, values) => this.handleValidSubmit(event, values)}
        className="broadcast-form"
      >
        <AvField
          label={t('formulary.title')}
          className="org-inputs"
          name="title"
          type="text"
          validate={{
            required: {
              value: true,
              errorMessage: t('formulary.validation.title_required'),
            },
            maxLength: {
              value: 100,
            },
          }}
        />
        <label>{t('formulary.message')}</label>
        <CKEditor
          editor={ClassicEditor}
          data=""
          config={{
            plugins: [Essentials, Bold, Italic, List, Heading, Paragraph],
            toolbar: [
              'heading',
              'bold',
              'italic',
              'bulletedList',
              'numberedList',
            ],
          }}
          onBlur={(event, editor) => {
            const content = editor.getData();
            this.setState({ message: content, messageError: false });
          }}
        />
        <div className="style-targets-row mt-3">
          <AvRadioGroup
            value={this.state.style}
            className="styles"
            inline
            name="style"
            required
            errorMessage={t('formulary.validation.select_style')}
          >
            <div className="radio-group-horiz">
              <AvRadio
                label={t('common.information')}
                value="info"
                onChange={() => this.setState({ style: 'info' })}
              />
              <AvRadio
                label={t('common.warning')}
                value="warning"
                onChange={() => this.setState({ style: 'warning' })}
              />
            </div>
          </AvRadioGroup>
          <div className="broadcast-targets">
            <AvCheckboxGroup name="targets">
              <AvCheckbox
                label={t('common.email')}
                value="email"
                checked={sendAsEmail}
                onChange={() => this.sendAsEmailToggle()}
              />
              <AvCheckbox
                label={t('common.dashboard')}
                value="dashboard"
                checked={sendToDashboard}
                onChange={() => this.sendToDashboardToggle()}
              />
            </AvCheckboxGroup>
          </div>
        </div>
        {targetsError ? (
          <div className="invalid-dropdown-input">
            {t('organisation.communication.errors.select_a_target')}
          </div>
        ) : null}
        {messageError ? (
          <div className="invalid-dropdown-input">
            {t('organisation.communication.errors.message_reqd')}
          </div>
        ) : null}
        <AvRadioGroup
          value={this.state.userType}
          inline
          name="userType"
          required
          errorMessage={t(
            'organisation.communication.errors.select_one_option_required'
          )}
        >
          <div className="radio-group-horiz mt-3">
            <AvRadio
              label={t('organisation.communication.send_to_all_users')}
              value="allUsers"
              onChange={() => this.setState({ userType: 'allUsers' })}
            />
            <AvRadio
              label={t('organisation.communication.send_to_selected_users')}
              value="selectedUsers"
              onChange={() => this.setState({ userType: 'selectedUsers' })}
            />
            <AvRadio
              label={t('organisation.communication.send_to_org_admins')}
              value="orgAdmins"
              onChange={() => this.setState({ userType: 'orgAdmins' })}
            />
            <AvRadio
              label={t('organisation.communication.send_to_myself')}
              value="testToMyself"
              onChange={() => this.setState({ userType: 'testToMyself' })}
            />
          </div>
        </AvRadioGroup>
        {this.state.userType === 'selectedUsers' ? (
          <div className="greenlight-broadcast">
            <label>
              {t('organisation.communication.select_users_heading')}
            </label>
            <Dropdown
              fluid
              multiple
              search
              selection
              options={options}
              onChange={(event, data) => this.onChangeUsers(event, data)}
              error={usersError}
              placeholder={t(
                'organisation.communication.select_users_placeholder'
              )}
              className="broadcast-dropdown"
            />
            {usersError ? (
              <div className="invalid-dropdown-input">
                {t('organisation.communication.errors.select_users_required')}
              </div>
            ) : null}
          </div>
        ) : null}
      </AvForm>
    );
  }
}

class CreateNotification extends PureComponent {
  constructor(props) {
    super(props);
    this.state = {
      modal: false,
    };
    this.notificationFormRef = createRef();
  }

  toggle() {
    this.setState((prevState) => ({
      modal: !prevState.modal,
    }));
  }

  render() {
    const { modal } = this.state;
    const { t, notificationsLoading } = this.props;

    return (
      <div className="add-goal">
        <Button onClick={() => this.toggle()} className="Primary-Button">
          {t('organisation.communication.notification_creation_trigger')}
        </Button>
        <SlideoutPanel
          open={modal}
          onClose={() => this.toggle()}
          title={t('organisation.communication.notification_creation_header')}
          className="org-broadcast-panel dialog-style1 dialog-width-lg"
          actions={
            <Fragment>
              <Button
                className="Primary-Button Medium"
                content={t('actions.send')}
                loading={notificationsLoading.broadcast}
                disabled={notificationsLoading.broadcast}
                onClick={() =>
                  this.notificationFormRef.current &&
                  this.notificationFormRef.current.submit()
                }
              />
              <Button
                className="Secondary-Button Medium"
                onClick={() => this.toggle()}
              >
                {t('actions.cancel')}
              </Button>
            </Fragment>
          }
        >
          <NotificationForm
            {...this.props}
            ref={this.notificationFormRef}
            toggle={() => this.toggle()}
          />
        </SlideoutPanel>
      </div>
    );
  }
}

class ViewNotification extends PureComponent {
  constructor(props) {
    super(props);
    this.state = {
      modal: false,
    };
  }

  async toggle() {
    await this.setState((prevState) => ({
      modal: !prevState.modal,
    }));

    const { records, updateNotificationReadStatus, isBroadcast } = this.props;
    const { modal } = this.state;
    if (modal && !isBroadcast && !records.isRead) {
      updateNotificationReadStatus(records.id);
    }
  }
  render() {
    const { modal } = this.state;
    const { t, records, isBroadcast, trigger } = this.props;

    return (
      <Modal
        trigger={trigger}
        size="small"
        open={modal}
        onOpen={() => this.toggle()}
        closeIcon={
          <Icon
            onClick={() => this.toggle()}
            name="close"
            className="closeicon"
          />
        }
        className="view-broadcast-message"
      >
        <Modal.Header>
          {t('organisation.communication.view_broadcast_message')}
        </Modal.Header>
        <Modal.Content className="pre-line">
          {HtmlService.convertToInnerHtml({
            children: isBroadcast
              ? get(records, 'message')
              : get(records, 'notification.message'),
            richText: true,
            htmlConversionStyle: 'para',
            returnAs: 'html',
          })}
        </Modal.Content>
      </Modal>
    );
  }
}

class Communication extends PureComponent {
  constructor(props) {
    super(props);
    const { t, i18n } = this.props;

    this.state = {
      currentPage: 1,
      columns: [
        {
          title: t('formulary.title'),
          width: '7',
          render: (data) => {
            return <label>{get(data, 'title', '-')}</label>;
          },
        },
        {
          title: t('formulary.sender'),
          width: '3',
          render: (data) => (
            <label>
              {get(data, 'sender.firstName', '-')} {get(data, 'sender.surname')}
            </label>
          ),
        },
        {
          title: t('common.created_date'),
          width: '4',
          render: (data) => {
            return (
              <label>
                {get(data, 'createdAt')
                  ? new Intl.DateTimeFormat(i18n.language, {
                      day: 'numeric', // 1
                      month: 'long', // September
                      year: 'numeric', // 2025
                      hour: '2-digit', // 09
                      minute: '2-digit', // 05
                      hour12: true, // AM/PM (always 12h format)
                    }).format(new Date(get(data, 'createdAt')))
                  : '-'}
              </label>
            );
          },
        },
        {
          title: '',
          width: '1',
          render: (data) => {
            return (
              <div className="org-checklist-actions">
                <ViewNotification
                  records={data}
                  trigger={
                    <label className="activity-actions-warn">
                      {t('actions.view')}
                    </label>
                  }
                  {...this.props}
                />
              </div>
            );
          },
        },
      ],
      notificationsColumns: [
        {
          title: t('formulary.title'),
          width: '7',
          render: (data) => {
            return (
              <div>
                <label>{get(data, 'notification.title', '-')}</label>
                {!get(data, 'isRead') ? (
                  <span className="new-label">
                    {t('organisation.communication.new_badge')}
                  </span>
                ) : null}
              </div>
            );
          },
        },
        {
          title: t('organisation.communication.sender'),
          width: '3',
          render: (data) => {
            return (
              <label>
                {get(data, 'notification.sender.firstName', '-')}{' '}
                {get(data, 'notification.sender.surname')}
              </label>
            );
          },
        },
        {
          title: t('common.created_date'),
          width: '4',
          render: (data) => {
            return (
              <label>
                {get(data, 'createdAt')
                  ? new Intl.DateTimeFormat(i18n.language, {
                      day: 'numeric', // 1
                      month: 'long', // September
                      year: 'numeric', // 2025
                      hour: '2-digit', // 09
                      minute: '2-digit', // 05
                      hour12: true, // AM/PM (always 12h format)
                    }).format(new Date(get(data, 'createdAt')))
                  : '-'}
              </label>
            );
          },
        },
        {
          title: '',
          width: '1',
          render: (data) => {
            return (
              <div className="org-checklist-actions">
                <ViewNotification
                  {...this.props}
                  records={data}
                  trigger={
                    <label className="activity-actions-warn">
                      {t('actions.view')}
                    </label>
                  }
                />
              </div>
            );
          },
        },
      ],
    };
  }

  resetPage() {
    this.setState({
      currentPage: 1,
    });
  }

  componentDidMount() {
    const {
      user,
      getBroadcastNotifications,
      getNotifications,
      getOrganizationUsers,
      isBroadcast,
    } = this.props;
    if (isBroadcast) {
      const page = 1;
      const limit = 10;
      const isOwnOrganisation = true;
      getBroadcastNotifications(page, limit, isOwnOrganisation);
      getOrganizationUsers(user.organisation.id);
    } else {
      getNotifications();
    }
  }

  async notificationsPageChange(page) {
    const { getNotifications } = this.props;
    this.setState({ currentPage: page }, () => {
      getNotifications(page);
    });
  }

  async broadcastPageChange(page) {
    const { getBroadcastNotifications } = this.props;
    this.setState({ currentPage: page }, () => {
      getBroadcastNotifications(page);
    });
  }

  render() {
    const { columns, notificationsColumns, currentPage } = this.state;
    const {
      t,
      isBroadcast,
      notificationsLoading,
      broadcastMessage,
      notifications,
    } = this.props;

    return (
      <Segment
        className="content-segment org-checklist"
        loading={
          isBroadcast
            ? notificationsLoading.broadcast
            : notificationsLoading.notification
        }
      >
        <Grid>
          {isBroadcast ? (
            <Grid.Row>
              <Grid.Column
                mobile={16}
                tablet={16}
                computer={16}
                textAlign="right"
                verticalAlign="bottom"
              >
                <div className="com-contact-btn">
                  <div>
                    <a
                      href={`mailto:${INSTANCE_CONTACT_EMAIL}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="Secondary-Button support-mail"
                    >
                      {t('organisation.communication.contact_support')}
                    </a>
                  </div>

                  <CreateNotification {...this.props} />
                </div>
              </Grid.Column>
            </Grid.Row>
          ) : null}
        </Grid>
        {isBroadcast ? (
          get(broadcastMessage, 'docs') && broadcastMessage.docs.length ? (
            <CustomTable
              header
              columns={columns}
              data={broadcastMessage.docs}
              customClass="org-checklist-table"
              pagination
              handlePaginationChange={(page) => this.broadcastPageChange(page)}
              page={currentPage}
              noOfPages={broadcastMessage.pages}
              keyExtractor={(item, index) => item.id}
            />
          ) : (
            <EmptyContainer
              msg={t('organisation.communication.no_notification_found')}
            />
          )
        ) : get(notifications, 'docs') && notifications.docs.length ? (
          <CustomTable
            header
            columns={notificationsColumns}
            data={notifications.docs}
            customClass="org-checklist-table"
            pagination
            handlePaginationChange={(page) =>
              this.notificationsPageChange(page)
            }
            page={currentPage}
            noOfPages={notifications.pages}
            keyExtractor={(item, index) => item.id}
          />
        ) : (
          <EmptyContainer
            msg={t('organisation.communication.no_notification_found')}
          />
        )}
      </Segment>
    );
  }
}

const mapStateToProps = (state) => {
  const { organization, authentication, user } = state;
  return {
    user: authentication.user,
    orgUsers: organization.orgUsers,
    notificationsLoading: user.notificationsLoading,
    broadcastMessage: user.broadcastMessage,
    notifications: user.notifications,
    organization: organization.organization,
  };
};

const mapDispatchToProps = (dispatch) => {
  return bindActionCreators(
    {
      getBroadcastNotifications,
      createBroadcastNotifications,
      getNotifications,
      getOrganizationUsers,
      updateNotificationReadStatus,
    },
    dispatch
  );
};

ViewNotification = withTranslation()(ViewNotification);

Communication = connect(
  mapStateToProps,
  mapDispatchToProps
)(withTranslation()(Communication));

export { ViewNotification, Communication };
